import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { PaginatedList } from 'src/app/_avmin/models/paginated-list';
import { environment } from 'src/environments/environment';
import { ISaleHistory } from '../models/sale-history.model';
import { DateTime } from 'luxon';
import { ISale, SaleStatus } from '../models/sale.model';
import { PaymentCardType } from '../models/payment-card-type.enum';

export const API_SALES_URL = `${environment.apiUrl}/sales`;

export interface SaleFilter {
  businessId?: number;
  customerId?: number;
  dateFrom?: DateTime;
  dateTo?: DateTime;
  status?: SaleStatus;
  orderBy: string;
  pageNo: number;
  pageSize: number;
  registerId?: string;
  salesPersonId?: number;
}

const defaultSaleFilter: SaleFilter = {
  orderBy: 'id',
  pageNo: 1,
  pageSize: 50
}

@Injectable({ providedIn: 'root' })
export class SalesService {
  private readonly _http = inject(HttpClient);


  acceptPayment(salePaymentId: number, cardType: PaymentCardType, reason: string): Observable<null> {
    return this._http.post<null>(`${API_SALES_URL}/payments/${salePaymentId}/accept`, { cardType, reason });
  }

  declinePayment(salePaymentId: number, reason: string): Observable<null> {
    return this._http.post<null>(`${API_SALES_URL}/payments/${salePaymentId}/decline`, { reason });
  }

  find(id: number): Observable<ISale> {
    return this._http.get<ISale>(`${API_SALES_URL}/${id}`);
  }

  emailInvoice(saleId: number, emailAddress?: string): Observable<void> {
    return this._http.post<undefined>(`${API_SALES_URL}/${saleId}/invoice/email`, { saleId, emailAddress });
  }

  get(partialFilter: Partial<SaleFilter>): Observable<PaginatedList<ISale>> {
    const filter: SaleFilter = { ...defaultSaleFilter, ...partialFilter };

    const q: any = {
      orderBy: filter.orderBy,
      pageNo: filter.pageNo,
      pageSize: filter.pageSize
    }

    if (filter.businessId !== undefined) { q.businessId = filter.businessId; }
    if (filter.customerId !== undefined) { q.customerId = filter.customerId; }
    if (filter.dateFrom !== undefined) { q.dateFrom = filter.dateFrom.toISO(); }
    if (filter.dateTo !== undefined) { q.dateTo = filter.dateTo.toISO(); }
    if (filter.registerId !== undefined) { q.registerId = filter.registerId; }
    if (filter.salesPersonId !== undefined) { q.salesPersonId = filter.salesPersonId; }
    if (filter.status !== undefined) { q.status = filter.status; }

    return this._http.get<PaginatedList<ISale>>(API_SALES_URL, { params: q });
  }

  getHistory(saleId: number): Observable<ISaleHistory[]> {
    return this._http.get<ISaleHistory[]>(`${API_SALES_URL}/${saleId}/history`);
  }

  getInvoicePdfUInt8Array(saleId: number): Observable<ArrayBuffer> {
    return this._http.get<ArrayBuffer>(`${API_SALES_URL}/${saleId}/invoice/pdf`, { responseType: 'arraybuffer' as 'json' });
  }

  parkOpen(): Observable<void> {
    return this._http.post<undefined>(`${API_SALES_URL}/park/open`, {});
  }

  void(saleId: number): Observable<ISale> {
    return this._http.post<ISale>(`${API_SALES_URL}/${saleId}/void`, {});
  }

}
